'use client';

import { useSnackbar } from 'notistack';
import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';

import Grid from '@mui/material/Grid';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import {
  Box,
  Stack,
  Button,
  Select,
  Divider,
  MenuItem,
  Typography,
  InputLabel,
  FormControl,
} from '@mui/material';

import Iconify from '../../../../components/iconify';
import { IMerchant } from '../../../../types/merchant';
import { BlockType } from '../../../../types/generator';
import { useBoolean } from '../../../../hooks/use-boolean';
import { useResponsive } from '../../../../hooks/use-responsive';
import { useLocales } from '../../../../locales';

const availableDates = [
  { date: '12/4', label: '12 באפריל' },
  { date: '13/4', label: '13 באפריל' },
  { date: '14/4', label: '14 באפריל' },
  { date: '15/4', label: '15 באפריל' },
  { date: '16/4', label: '16 באפריל' },
  { date: '17/4', label: '17 באפריל' },
  { date: '18/4', label: '18 באפריל' },
  { date: '19/4', label: '19 באפריל' },
  { date: '20/4', label: '20 באפריל' },
  { date: '21/4', label: '21 באפריל' },
  { date: '22/4', label: '22 באפריל' },
  { date: '23/4', label: '23 באפריל' },
  { date: '24/4', label: '24 באפריל' },
  { date: '25/4', label: '25 באפריל' },
  {
    date: '26/4',
    label: '26 באפריל',
    info: '😌 ערב חג פסח סגור',
    time: '15:30',
    disabled: false,
  },
  {
    date: '27/4',
    label: '27 באפריל',
    info: '😔 עד 17:30 ערב חג פסח סגור',
    time: '',
    disabled: true,
  },
  { date: '28/4', label: '28 באפריל' },
  { date: '29/4', label: '29 באפריל' },
  { date: '30/4', label: '30 באפריל' },
  // ... далее автоматически сгенерированные даты до 31 августа
];
const availableTimes = ['12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00'];
const guestOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const locationOptions = ['פנים', 'חוץ', 'בר'];
const countries = [{ code: 'IL', label: '🇮🇱 ישראל' }];

type Props = {
  merchant?: IMerchant | undefined | any;
  close?: () => void;
  block?: BlockType | undefined;
};

const BookingForm = ({ merchant, close, block }: Props) => {
  const { currentLang } = useLocales();
  const isMobile = useResponsive('down', 'sm');
  const modalSuccess = useBoolean();
  const { enqueueSnackbar } = useSnackbar();
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [guests, setGuests] = useState<number | ''>('');
  const [location, setLocation] = useState('');
  const [showHebrewForm, setShowHebrewForm] = useState(false); // ✅
  const [countdown, setCountdown] = useState(300); // 5 минут
  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm();

  const values = watch();

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (showHebrewForm) {
      setCountdown(300); // Сброс таймера при открытии
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setShowHebrewForm(false); // Возвращаем форму выбора
            enqueueSnackbar('הזמן להזמנה פג. אנא נסה שוב.', { variant: 'warning' });
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [showHebrewForm]);

  const onSubmit = (data: any) => {
    modalSuccess.onTrue();
    setShowHebrewForm(false);
    // alert(JSON.stringify(data, null, 2));
  };

  const isFormComplete = date && time && guests && location;

  if (showHebrewForm) {
    return (
      <Box
        sx={{
          direction: 'rtl',
          maxWidth: 600,
          mx: 'auto',
          background: 'white',
          p: 2,
          borderRadius: 5,
        }}
      >
        <Iconify
          icon="material-symbols:close-rounded"
          onClick={() => close && close()}
          sx={
            currentLang.value === 'en'
              ? {
                  cursor: 'pointer',
                  position: 'absolute',
                  top: 10,
                  right: 10,
                }
              : { cursor: 'pointer', position: 'absolute', top: 10, left: 10 }
          }
        />
        <Typography sx={{ textAlign: 'center' }} variant="h6" gutterBottom>
          להשלמת התהליך אנא מלאו את הפרטים הבאים
        </Typography>
        <Typography variant="body2" textAlign="center" sx={{ mb: 2, color: 'grey' }}>
          זמן שנותר:{' '}
          {Math.floor(countdown / 60)
            .toString()
            .padStart(2, '0')}
          :{(countdown % 60).toString().padStart(2, '0')}
        </Typography>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Controller
                name="firstName"
                control={control}
                rules={{ required: 'שדה חובה' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="שם פרטי *"
                    fullWidth
                    error={!!errors.firstName}
                    // @ts-ignore
                    helperText={errors.firstName?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="lastName"
                control={control}
                rules={{ required: 'שדה חובה' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="שם משפחה *"
                    fullWidth
                    error={!!errors.lastName}
                    // @ts-ignore
                    helperText={errors.lastName?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={4}>
              <Controller
                name="country"
                control={control}
                defaultValue="IL"
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>אִישׁ</InputLabel>
                    <Select {...field}>
                      {countries.map((option) => (
                        <MenuItem key={option.code} value={option.code}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={8}>
              <Controller
                name="phone"
                control={control}
                rules={{ required: 'שדה חובה' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="טלפון *"
                    fullWidth
                    error={!!errors.phone}
                    // @ts-ignore
                    helperText={errors.phone?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="אימייל" type="email" fullWidth />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField label="הערות" fullWidth multiline rows={2} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center' }}>
                לחיצה על הכפתור תאשר את תנאי השימוש ומדיניות הפרטיות
              </Typography>
            </Grid>
            <Grid item xs={12} textAlign="center">
              <Button type="submit" variant="contained" sx={{ px: 6, py: 1.5 }}>
                שמירה ✔
              </Button>
            </Grid>
          </Grid>
        </form>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 600, mx: 'auto', backgroundColor: 'white', borderRadius: 5 }}>
      {!modalSuccess.value ? (
        <>
          <Iconify
            icon="material-symbols:close-rounded"
            onClick={() => close && close()}
            sx={
              currentLang.value === 'en'
                ? {
                    cursor: 'pointer',
                    position: 'absolute',
                    top: 10,
                    right: 10,
                  }
                : { cursor: 'pointer', position: 'absolute', top: 10, left: 10 }
            }
          />
          <Stack alignItems="center">
            <Typography variant="h6">booking</Typography>
            <Typography>{merchant?.title || 'Merchant'}</Typography>
          </Stack>
          <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
            {/* Date select */}
            <FormControl variant="outlined" sx={{ minWidth: '33%' }}>
              <InputLabel>תאריך</InputLabel>
              <Select
                value={date}
                onChange={(e) => {
                  const selectedDate = e.target.value;
                  const [day, month] = selectedDate.split('/').map(Number);
                  const selected = new Date(2025, month - 1, day);
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);

                  if (selected <= today) {
                    const nextDate = availableDates.find((d) => {
                      const [dDay, dMonth] = d.date.split('/').map(Number);
                      const dDate = new Date(2025, dMonth - 1, dDay);
                      return dDate > today && !d.disabled;
                    });
                    if (nextDate) setDate(nextDate.date);
                  } else {
                    setDate(selectedDate);
                  }
                }}
                label="תאריך"
                renderValue={(selected) => {
                  const selectedObj = availableDates.find((d) => d.date === selected);
                  return selectedObj?.label || selected;
                }}
              >
                {availableDates.map((d) => (
                  <MenuItem key={d.date} value={d.date} disabled={d.disabled}>
                    <Stack direction="column">
                      <span>{d.label}</span>
                      {d.info && (
                        <Typography variant="caption" color="text.secondary">
                          {d.time && `${d.time} `}
                          {d.info}
                        </Typography>
                      )}
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Time select */}
            <FormControl variant="outlined" sx={{ minWidth: '33%' }}>
              <InputLabel>שעה</InputLabel>
              <Select value={time} onChange={(e) => setTime(e.target.value)} label="שעה">
                {availableTimes.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Guests select */}
            <FormControl variant="outlined" sx={{ minWidth: '33%' }}>
              <InputLabel>אורחים</InputLabel>
              <Select
                value={guests}
                onChange={(e) => setGuests(Number(e.target.value))}
                label="אורחים"
              >
                {guestOptions.map((g) => (
                  <MenuItem key={g} value={g}>
                    {g} אורחים
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" gutterBottom>
            בחרו העדפה *
          </Typography>

          <Stack direction="row" spacing={1}>
            {locationOptions.map((loc) => (
              <Button
                key={loc}
                variant={loc === location ? 'contained' : 'outlined'}
                onClick={() => setLocation(loc)}
                sx={{ borderRadius: '5px !important' }}
              >
                {loc}
              </Button>
            ))}
          </Stack>

          <Button
            fullWidth
            variant="contained"
            color="success"
            sx={{ mt: 3 }}
            disabled={!isFormComplete}
            onClick={() => {
              enqueueSnackbar(
                `תאריך: ${date}, שעה: ${time}, אורחים: ${guests}, מיקום: ${location}`,
                {
                  variant: 'success',
                }
              );
              setShowHebrewForm(true); // ✅ Показываем HebrewForm
            }}
          >
            אשר הזמנה
          </Button>
        </>
      ) : (
        <>
          <Iconify
            icon="material-symbols:close-rounded"
            onClick={() => {
              if (close) {
                close();
                modalSuccess.onFalse();
              }
            }}
            sx={
              currentLang.value === 'en'
                ? {
                    cursor: 'pointer',
                    position: 'absolute',
                    top: 10,
                    right: 10,
                  }
                : { cursor: 'pointer', position: 'absolute', top: 10, left: 10 }
            }
          />
          <Typography>
            Hello {values.firstName} {values.lastName}
          </Typography>
          <Typography>Your order has been successfully saved with us.</Typography>
          <Typography>
            {' '}
            Your table will be saved for 15 minutes from the time of your order.
          </Typography>
          <Typography>We look forward to seeing you!</Typography>
          <Stack direction="row" gap={1} justifyContent="space-between" mt={1}>
            <Button
              variant="contained"
              onClick={() => {
                if (close) {
                  close();
                }
                modalSuccess.onFalse();
              }}
            >
              Update
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                if (close) {
                  close();
                }
                modalSuccess.onFalse();
              }}
            >
              Cancel
            </Button>
          </Stack>
        </>
      )}
    </Box>
  );
};

export default BookingForm;
